import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { describeState, type FsrsStateJson } from "@/lib/vocab/scheduler";

/**
 * /learning/vocabulary/stats (BW18 PR-J, multilingual via HOTFIX-BW19.4
 * PR-A Bug 8).
 *
 * Three panels: state breakdown, retention rate over 30 days,
 * top-lapse cards. The retention rate = % of reviews not rated "Again"
 * over the trailing 7 days. State + lapse counts now read from each
 * vocab_translation row so a multi-direction card contributes once
 * per active language.
 */

type FsrsState = NonNullable<FsrsStateJson["state"]>;

type CardLite = {
  id: string;
  state: FsrsState;
  lapses: number;
  source_text: string;
  target_text: string;
  target_lang: string;
};

type ReviewLite = {
  reviewed_at: string;
  rating: number;
};

export function LearningVocabularyStats() {
  const cards = useCards();
  const reviews = useReviews(30);

  const stateCounts = useMemo(() => {
    const init: Record<FsrsState, number> = {
      new: 0,
      learning: 0,
      review: 0,
      relearning: 0,
    };
    for (const c of cards.data ?? []) {
      init[c.state] = (init[c.state] ?? 0) + 1;
    }
    return init;
  }, [cards.data]);

  const retention = useMemo(() => buildRetention(reviews.data ?? []), [reviews.data]);
  const topLapses = useMemo(() => {
    const list = (cards.data ?? []).filter((c) => c.lapses > 0);
    list.sort((a, b) => b.lapses - a.lapses);
    return list.slice(0, 8);
  }, [cards.data]);

  return (
    <article aria-labelledby="vocab-stats-heading">
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        <Link to="/learning/vocabulary" style={{ color: "var(--fg-3)" }}>
          Vocabulary
        </Link>{" "}
        · Stats
      </p>
      <h1
        id="vocab-stats-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        How well it's sticking.
      </h1>

      <div style={{ display: "grid", gap: "var(--space-5)" }}>
        <StateBreakdown counts={stateCounts} />
        <RetentionPanel data={retention} />
        <TopLapsesPanel rows={topLapses} />
      </div>
    </article>
  );
}

function StateBreakdown({ counts }: { counts: Record<FsrsState, number> }) {
  const total =
    counts.new + counts.learning + counts.review + counts.relearning;
  return (
    <section style={panelStyle()}>
      <h2 style={panelHeadingStyle()}>Card states</h2>
      {total === 0 ? (
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          No cards yet.{" "}
          <Link to="/learning/vocabulary/import" style={{ color: "var(--accent)" }}>
            Import some
          </Link>
          .
        </p>
      ) : (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "var(--space-3)",
            margin: 0,
          }}
        >
          {(["new", "learning", "review", "relearning"] as const).map((s) => (
            <div key={s}>
              <dt style={labelStyle()}>{describeState(s)}</dt>
              <dd
                className="mono"
                style={{
                  margin: "2px 0 0",
                  fontSize: "var(--type-body-l)",
                  color: "var(--fg)",
                }}
              >
                {counts[s]}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

function RetentionPanel({
  data,
}: {
  data: { points: Array<{ date: string; pct: number; n: number }>; rolling: number | null };
}) {
  return (
    <section style={panelStyle()}>
      <header style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <h2 style={panelHeadingStyle()}>Retention rate</h2>
        <span className="mono" style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
          {data.rolling != null ? `7d ${(data.rolling * 100).toFixed(0)}%` : "—"}
        </span>
      </header>
      {data.points.length === 0 ? (
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          Review cards to start the retention curve.
        </p>
      ) : (
        <div style={{ height: 200 }}>
          <ResponsiveContainer>
            <LineChart data={data.points}>
              <XAxis
                dataKey="date"
                tick={{ fill: "var(--fg-3)", fontSize: 11 }}
                stroke="var(--hair-2)"
                tickFormatter={(d: string) => d.slice(5)}
              />
              <YAxis
                domain={[0, 1]}
                tick={{ fill: "var(--fg-3)", fontSize: 11 }}
                stroke="var(--hair-2)"
                tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--bg-3)",
                  border: "1px solid var(--hair-2)",
                  borderRadius: 6,
                  fontSize: 12,
                }}
                formatter={(value: number) => `${(value * 100).toFixed(0)}%`}
                labelFormatter={(d: string) => d}
              />
              <Line
                type="monotone"
                dataKey="pct"
                stroke="var(--accent)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

function TopLapsesPanel({ rows }: { rows: CardLite[] }) {
  return (
    <section style={panelStyle()}>
      <h2 style={panelHeadingStyle()}>Hardest cards</h2>
      {rows.length === 0 ? (
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          No lapses yet.
        </p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: 4,
          }}
        >
          {rows.map((r) => (
            <li
              key={r.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr max-content",
                padding: "6px 0",
                borderBottom: "1px solid var(--hair)",
                color: "var(--fg)",
                fontSize: "var(--type-body-s)",
              }}
            >
              <span>
                {r.source_text} <span style={{ color: "var(--fg-3)" }}>→ {r.target_text}</span>
              </span>
              <span className="mono" style={{ color: "var(--brick)" }}>
                {r.lapses} lapse{r.lapses === 1 ? "" : "s"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function buildRetention(
  reviews: ReviewLite[],
): { points: Array<{ date: string; pct: number; n: number }>; rolling: number | null } {
  const byDay = new Map<string, { good: number; n: number }>();
  for (const r of reviews) {
    const date = r.reviewed_at.slice(0, 10);
    const acc = byDay.get(date) ?? { good: 0, n: 0 };
    acc.n += 1;
    if (r.rating >= 2) acc.good += 1;
    byDay.set(date, acc);
  }
  const points = [...byDay.entries()]
    .map(([date, { good, n }]) => ({ date, pct: good / n, n }))
    .sort((a, b) => a.date.localeCompare(b.date));
  const last7 = points.slice(-7);
  const totalN = last7.reduce((s, p) => s + p.n, 0);
  const totalGood = last7.reduce((s, p) => s + p.pct * p.n, 0);
  return {
    points,
    rolling: totalN > 0 ? totalGood / totalN : null,
  };
}

function useCards() {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["vocab_stats_translations", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<CardLite[]> => {
      if (!userId) return [];
      type Joined = {
        id: string;
        target_lang: string;
        target_text: string;
        fsrs_state: FsrsStateJson | null;
        vocab_card: { source_text: string } | null;
      };
      const { data, error } = await supabase
        .from("vocab_translation")
        .select(
          "id, target_lang, target_text, fsrs_state, vocab_card!inner(source_text)",
        )
        .eq("user_id", userId);
      if (error) throw error;
      const rows = (data ?? []) as unknown as Joined[];
      return rows.map((r) => ({
        id: r.id,
        target_lang: r.target_lang,
        target_text: r.target_text,
        state: r.fsrs_state?.state ?? "new",
        lapses: r.fsrs_state?.lapses ?? 0,
        source_text: r.vocab_card?.source_text ?? "",
      }));
    },
  });
}

function useReviews(days: number) {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["vocab_stats_reviews", userId, days],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<ReviewLite[]> => {
      if (!userId) return [];
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error } = await supabase
        .from("vocab_review")
        .select("reviewed_at, rating")
        .eq("user_id", userId)
        .gte("reviewed_at", since.toISOString())
        .order("reviewed_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ReviewLite[];
    },
  });
}

function panelStyle(): React.CSSProperties {
  return {
    background: "var(--bg-2)",
    border: "1px solid var(--hair)",
    borderRadius: "var(--radius-card)",
    padding: "var(--space-5)",
    display: "grid",
    gap: "var(--space-3)",
  };
}

function panelHeadingStyle(): React.CSSProperties {
  return {
    margin: 0,
    fontSize: "var(--type-body-l)",
    fontWeight: 500,
  };
}

function labelStyle(): React.CSSProperties {
  return {
    fontSize: "var(--type-meta)",
    color: "var(--fg-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  };
}
